import { useMutation, useQueryClient } from 'react-query';

import { EPresence, type EPresenceDuration } from 'src/api/users-api';
import { apiClient } from 'src/services/api';
import { queryKeys } from 'src/services/api/constants';
import { useNotifications } from 'src/notifications';

type Request = { presence: EPresence; duration?: EPresenceDuration };

/**
 * Sets the current user's presence (online / away) as a manual override. The
 * override lasts for the given duration (default: the rest of the day), after
 * which presence reverts to the user's working hours. A user can be away during
 * their working hours or online outside them. On success both the team roster
 * and the logged-in user are refetched so every presence indicator stays in sync.
 */
export const useSetPresence = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<void, unknown, Request>(
        async ({ presence, duration }) => {
            await apiClient('api/users/me/presence', {
                method: 'put',
                json: duration ? { presence, duration } : { presence },
            });
        },
        {
            onSuccess: (_data, { presence }) => {
                queryClient.invalidateQueries([queryKeys.teamMembers]);
                queryClient.invalidateQueries([queryKeys.loggedInUser]);
                addNotification(
                    presence === EPresence.AWAY ? 'You are now away' : 'You are now online',
                    'success',
                );
            },
            onError: () => {
                addNotification('Failed to update your status. Please try again.', 'error');
            },
        },
    );
};

export default useSetPresence;
